import { SingularValueDecomposition } from "ml-matrix";

import { createCameraMatrix, fundamentalToEssential } from "./geometry";

export function estimateSharedFocalLength({
  fundamentalMatrix,
  widthA,
  heightA,
  widthB,
  heightB,
}) {
  const referenceSize = Math.max(widthA, heightA, widthB, heightB);

  function evaluateFocalLength(focalLength) {
    const cameraMatrixA = createCameraMatrix(widthA, heightA, focalLength);
    const cameraMatrixB = createCameraMatrix(widthB, heightB, focalLength);

    const rawEssentialMatrix = fundamentalToEssential(
      fundamentalMatrix,
      cameraMatrixA,
      cameraMatrixB,
      false,
    );

    const svd = new SingularValueDecomposition(rawEssentialMatrix);
    const singularValues = [...svd.diagonal].sort((a, b) => b - a);

    const sigma1 = singularValues[0];
    const sigma2 = singularValues[1];
    const sigma3 = singularValues[2];

    if (
      !Number.isFinite(sigma1) ||
      !Number.isFinite(sigma2) ||
      !Number.isFinite(sigma3) ||
      sigma2 <= 1e-12
    ) {
      return null;
    }

    const equalSingularPenalty =
      Math.abs(sigma1 - sigma2) / Math.max(sigma1, sigma2);
    const rankPenalty = sigma3 / sigma2;

    return {
      focalLength,
      focalRatio: focalLength / referenceSize,
      score: equalSingularPenalty + rankPenalty,
      equalSingularPenalty,
      rankPenalty,
      singularValues,
      cameraMatrixA,
      cameraMatrixB,
      rawEssentialMatrix,
    };
  }

  // Coarse search
  const coarseResults = [];

  for (
    let focalLength = referenceSize * 0.2;
    focalLength <= referenceSize * 3.0;
    focalLength += referenceSize * 0.025
  ) {
    try {
      const result = evaluateFocalLength(focalLength);

      if (result) {
        coarseResults.push(result);
      }
    } catch {
      // Ignore degenerate candidates
    }
  }

  if (coarseResults.length === 0) {
    return null;
  }

  coarseResults.sort((a, b) => a.score - b.score);

  const coarseBest = coarseResults[0];

  // Fine search
  const coarseRadius = referenceSize * 0.025;
  const fineStep = referenceSize * 0.001;

  const fineStart = Math.max(
    referenceSize * 0.1,
    coarseBest.focalLength - coarseRadius,
  );

  const fineEnd = coarseBest.focalLength + coarseRadius;
  const fineResults = [];

  for (
    let focalLength = fineStart;
    focalLength <= fineEnd;
    focalLength += fineStep
  ) {
    try {
      const result = evaluateFocalLength(focalLength);

      if (result) {
        fineResults.push(result);
      }
    } catch {
      // Ignore degenerate candidates
    }
  }

  fineResults.sort((a, b) => a.score - b.score);

  const best = fineResults[0] ?? coarseBest;

  return {
    referenceSize,
    coarseCandidates: coarseResults.length,
    fineCandidates: fineResults.length,
    coarseBest,
    best,
    results: [...coarseResults, ...fineResults],
  };
}
